// src/services/postService.js

import api from "./apiService";

/**
 * Fetch posts for current user's community
 * @param {number} page Page number
 * @param {number} perPage Posts per page
 * @returns {Promise<Object>} Posts data with pagination info
 */
export const fetchPosts = async (page = 1, perPage = 10) => {
    try {
        const response = await api.get("/posts", {
            params: { page, per_page: perPage }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch a single post with its comments
 * @param {number} postId Post ID
 * @returns {Promise<Object>} Post data with comments
 */
export const fetchPost = async (postId) => {
    try {
        const response = await api.get(`/posts/${postId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new post
 * @param {Object} postData Post data (title, content)
 * @returns {Promise<Object>} Created post data
 */
export const createPost = async (postData) => {
    try {
        // Get CSRF token
        const csrfToken = localStorage.getItem("csrfToken");
        
        // Add CSRF token to request data
        const dataWithCsrf = {
            ...postData,
            csrf_token: csrfToken
        };
        
        const response = await api.post("/posts", dataWithCsrf, {
            headers: { "X-CSRFToken": csrfToken }
        });
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing post
 * @param {number} postId Post ID
 * @param {Object} postData Updated post data
 * @returns {Promise<Object>} Updated post data
 */
export const updatePost = async (postId, postData) => {
    try {
        // Get CSRF token
        const csrfToken = localStorage.getItem("csrfToken");
        
        // Add CSRF token to request data
        const dataWithCsrf = {
            ...postData,
            csrf_token: csrfToken
        };
        
        const response = await api.put(`/posts/${postId}`, dataWithCsrf, {
            headers: { "X-CSRFToken": csrfToken }
        });
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a post
 * @param {number} postId Post ID
 * @returns {Promise<Object>} Response data
 */
export const deletePost = async (postId) => {
    try {
        // Get CSRF token
        const csrfToken = localStorage.getItem("csrfToken");
        
        // Create data with CSRF token for DELETE request
        const dataWithCsrf = {
            csrf_token: csrfToken
        };
        
        const response = await api.delete(`/posts/${postId}`, {
            headers: { "X-CSRFToken": csrfToken },
            data: dataWithCsrf
        });
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new comment
 * @param {Object} commentData Comment data (content, content_type, content_id)
 * @returns {Promise<Object>} Created comment data
 */
export const createComment = async (commentData) => {
    try {
        // Get CSRF token
        const csrfToken = localStorage.getItem("csrfToken");
        
        // Add CSRF token to request data
        const dataWithCsrf = {
            ...commentData,
            csrf_token: csrfToken
        };
        
        const response = await api.post("/comments", dataWithCsrf, {
            headers: { "X-CSRFToken": csrfToken }
        });
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing comment
 * @param {number} commentId Comment ID
 * @param {Object} commentData Updated comment data
 * @returns {Promise<Object>} Updated comment data
 */
export const updateComment = async (commentId, commentData) => {
    try {
        // Get CSRF token
        const csrfToken = localStorage.getItem("csrfToken");
        
        // Add CSRF token to request data
        const dataWithCsrf = {
            ...commentData,
            csrf_token: csrfToken
        };
        
        const response = await api.put(`/comments/${commentId}`, dataWithCsrf, {
            headers: { "X-CSRFToken": csrfToken }
        });
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete a comment
 * @param {number} commentId Comment ID
 * @returns {Promise<Object>} Response data
 */
export const deleteComment = async (commentId) => {
    try {
        // Get CSRF token
        const csrfToken = localStorage.getItem("csrfToken");
        
        // Create data with CSRF token for DELETE request
        const dataWithCsrf = {
            csrf_token: csrfToken
        };
        
        const response = await api.delete(`/comments/${commentId}`, {
            headers: { "X-CSRFToken": csrfToken },
            data: dataWithCsrf
        });
        
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Add this to postService.js

/**
 * Fetch comments for a specific content type and ID
 * @param {Object} params - Parameters for fetching comments
 * @param {string} params.content_type - Type of content ('post', 'event', etc.)
 * @param {number} params.content_id - ID of the content
 * @returns {Promise<Array>} Array of comments
 */
export const getComments = async (params) => {
    try {
      const response = await api.get('/comments', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  };